'use client';

import { useObservable, useObserve } from '@legendapp/state/react';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import { type DetailedHTMLProps, type HTMLAttributes, type ReactNode, createContext, useContext, useEffect, useRef } from 'react';
import { type Observable, ObservableHint, batch } from '@legendapp/state';
import { cn } from '@/lib/cn';
import { CarouselNavigationButton } from './CarouselNavigationButtons';
import { CarouselNavigationContainer } from './CarouselNavigationContainer';
import { CarouselPagination, type CarouselPaginationButtonColor } from './CarouselPagination';
import { CarouselPaginationContainer } from './CarouselPaginationContainer';
export type CarouselApi = Exclude<UseEmblaCarouselType[1], undefined>;
export type CarouselOptions = Parameters<typeof useEmblaCarousel>[0];
interface CarouselState {
  active: boolean;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  selectedIndex: number;
  scrollSnaps: number[];
  emblaApi?: CarouselApi;
}
const carouselContext = createContext<Observable<CarouselState>>(undefined as unknown as Observable<CarouselState>);
export interface CarouselProps extends Omit<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, 'onSelect'> {
  options?: CarouselOptions;
  spacing?: number;
  showNavigation?: boolean;
  showPagination?: boolean;
  hideOverflow?: boolean;
  navContainerClassName?: string;
  navClassName?: string;
  paginationColor?: CarouselPaginationButtonColor;
  paginationClassName?: string;
  viewPortStyle?: HTMLAttributes<HTMLDivElement>['style'];
  header?: ReactNode;
  footer?: ReactNode;
  initClassName?: string;
  onSelect?: (embla: CarouselApi) => void;
}
export function Carousel({
  className,
  children,
  header,
  footer,
  options = {
    loop: false,
    slidesToScroll: 1
  },
  spacing = 32,
  showNavigation = true,
  showPagination = true,
  hideOverflow = true,
  viewPortStyle,
  style,
  navContainerClassName,
  navClassName,
  paginationColor,
  paginationClassName,
  initClassName,
  onSelect,
  ...props
}: CarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel(options, []);
  const carouselStateRef = useRef(useObservable({
    active: resolveActiveCarouselOptions(options).active,
    canScrollPrev: false,
    canScrollNext: false,
    selectedIndex: options?.startIndex || 0,
    scrollSnaps: []
  } as CarouselState));
  useEffect(() => {
    const carouselState$ = carouselStateRef.current;
    if (!emblaApi) {
      return;
    }
    carouselState$.emblaApi.set(ObservableHint.opaque(emblaApi));
  }, [emblaApi]);
  useObserve(() => {
    const carouselState$_0 = carouselStateRef.current;
    const api = carouselState$_0.emblaApi.get() as CarouselApi | undefined;
    if (!api || !api?.scrollSnapList) {
      return;
    }
    const updateState = (emblaApi_0: CarouselApi) => {
      if (!emblaApi_0) {
        return;
      }
      batch(() => {
        carouselState$_0.selectedIndex.set(emblaApi_0.selectedScrollSnap());
        carouselState$_0.canScrollPrev.set(emblaApi_0.canScrollPrev());
        carouselState$_0.canScrollNext.set(emblaApi_0.canScrollNext());
      });
    };
    const onInit = (emblaApi_1: CarouselApi) => {
      if (!emblaApi_1) {
        return;
      }
      carouselState$_0.scrollSnaps.set(emblaApi_1.scrollSnapList());
    };
    const handleSelect = (emblaApi_2: CarouselApi) => {
      onSelect?.(emblaApi_2);
      updateState(emblaApi_2);
    };
    onInit(api);
    updateState(api);
    api.on('reInit', onInit).on('select', handleSelect).on('resize', updateState);
  });
  useEffect(() => {
    if (!options?.breakpoints) {
      return;
    }
    const controller = new AbortController();
    for (const [breakPoint, breakpointOps] of Object.entries(options.breakpoints)) {
      const mediaQuery = getMediaQuery(breakPoint);
      const handleChange = (e?: MediaQueryListEvent) => {
        const carouselState$_1 = carouselStateRef.current;
        const active = mergeOptions(options, e?.matches ? breakpointOps : {}).active;
        carouselState$_1.active.set(active);
      };
      mediaQuery?.addEventListener('change', handleChange, {
        signal: controller.signal
      });
    }
    return () => {
      controller.abort();
    };
  }, [options]);
  return <carouselContext.Provider value={/* eslint-disable-line react-hooks/refs */carouselStateRef.current}>
            <div className={cn('relative [--carousel-spacing:var(--carousel-internal-spacing)]', {
      'mb-12': showPagination,
      ...(initClassName ? {
        [initClassName]: !Boolean(emblaApi)
      } : {})
    }, className)} style={{
      '--carousel-internal-spacing': `${spacing}px`,
      ...style
    }} {...props}>
                {header}
                <div className={cn('carousel-viewport', {
        'overflow-hidden': hideOverflow
      })} ref={emblaRef} data-testid="carousel-viewport" style={viewPortStyle}>
                    <div className="carousel-container flex flex-nowrap touch-pan-y transform-gpu">{children}</div>
                </div>
                {showNavigation ? <CarouselNavigationContainer className={navContainerClassName}>
                        <CarouselNavigationButton className={cn('inline-flex pointer-events-auto', navClassName)} variant="prev" aria-label="Previous slide" />
                        <CarouselNavigationButton className={cn('inline-flex pointer-events-auto', navClassName)} variant="next" aria-label="Next slide" />
                    </CarouselNavigationContainer> : null}
                {showPagination ? <CarouselPaginationContainer className={paginationClassName}>
                        <CarouselPagination color={paginationColor} />
                    </CarouselPaginationContainer> : null}
                {footer}
            </div>
        </carouselContext.Provider>;
}
export function useCarouselStateState() {
  const context = useContext(carouselContext);
  if (!context) {
    throw new Error("useCarouselStateState must be used within a Carousel");
  }
  return context;
}
const mediaQueryCache = new Map<string, MediaQueryList>();
function getMediaQuery(breakpoint: string): MediaQueryList | undefined {
  // Using globalThis as a workaround for the 'typeof window' bug. (https://github.com/storybookjs/storybook/issues/32028)
  if (!('window' in globalThis)) {
    return undefined;
  }
  if (!mediaQueryCache.has(breakpoint)) {
    mediaQueryCache.set(breakpoint, window.matchMedia(breakpoint));
  }
  return mediaQueryCache.get(breakpoint)!;
}
function resolveActiveCarouselOptions(options: CarouselOptions) {
  const breakPointOptions = options?.breakpoints;
  // Using globalThis as a workaround for the 'typeof window' bug. (https://github.com/storybookjs/storybook/issues/32028)
  if (!breakPointOptions || !('window' in globalThis)) {
    return mergeOptions(options);
  }
  const activeOptions: CarouselOptions[] = [];
  for (const [breakPoint, opts] of Object.entries(breakPointOptions)) {
    if (getMediaQuery(breakPoint)?.matches) {
      activeOptions.push(opts);
    }
  }
  return mergeOptions(options, ...activeOptions);
}
function mergeOptions(...options: CarouselOptions[]): CarouselOptions & {
  active: boolean;
} {
  return Object.assign({
    active: true
  }, ...options);
}